import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { createPage, generatePageSlug } from "@/lib/api/pages.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { CraftEditor, type CraftEditorHandle } from "@/components/admin/CraftEditor";
import { ContentAdminTabs } from "@/components/admin/ContentAdminTabs";
import { PageSettingsForm } from "@/components/admin/PageSettingsForm";
import { toast } from "sonner";
import type { MediaSelection } from "@/lib/media/types";
import { isReservedPageSlug } from "@/lib/craft/reserved-slugs";
import { resolveCraftForSave } from "@/lib/craft/use-craft-save";

export const Route = createFileRoute("/admin/pages/new")({
  component: NewPageAdmin,
});

function NewPageAdmin() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [craftState, setCraftState] = useState<string>("");
  const [craftCustomCss, setCraftCustomCss] = useState("");
  const [ogImage, setOgImage] = useState<MediaSelection>({});
  const [saving, setSaving] = useState(false);
  const craftEditorRef = useRef<CraftEditorHandle>(null);

  async function handleSave() {
    if (slug && isReservedPageSlug(slug) && slug !== "home" && slug !== "about") {
      toast.error(`Slug "${slug}" is reserved for an existing route.`);
      return;
    }
    const craft = resolveCraftForSave(craftEditorRef, { craftState, craftCustomCss });
    setSaving(true);
    try {
      const row = await createPage({
        data: {
          title,
          slug,
          status,
          craftState: craft.serialized,
          craftCustomCss: craft.customCss,
          metaTitle,
          metaDescription,
          ogImageMediaId: ogImage.mediaId,
        },
      });
      toast.success("Page created");
      navigate({ to: "/admin/pages/$id", params: { id: row.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="New page" />
      <ContentAdminTabs
        settingsLabel="Page settings"
        content={
          <CraftEditor
            ref={craftEditorRef}
            className="w-full"
            onChange={(serialized, css) => {
              setCraftState(serialized);
              setCraftCustomCss(css);
            }}
            onCustomCssChange={setCraftCustomCss}
            customCss={craftCustomCss}
          />
        }
        settingsForm={
          <PageSettingsForm
            title={title}
            onTitleChange={setTitle}
            slug={slug}
            onSlugChange={setSlug}
            onGenerateSlug={() => generatePageSlug({ data: { title } })}
            status={status}
            onStatusChange={setStatus}
            metaTitle={metaTitle}
            onMetaTitleChange={setMetaTitle}
            metaDescription={metaDescription}
            onMetaDescriptionChange={setMetaDescription}
            ogImage={ogImage}
            onOgImageChange={setOgImage}
          />
        }
      />
      <AdminFormFooter cancelTo="/admin/pages" onSave={handleSave} saving={saving} saveLabel="Create page" savingLabel="Creating…" />
    </AdminPageShell>
  );
}
